import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteAttachment,
  getAttachments,
  getPendingAttachments,
  replaceAttachmentContent,
  updateAttachment,
  uploadAttachment,
} from "../api/attachments";
import type {
  AttachmentDto,
  AttachmentType,
  UpdateAttachmentDto,
} from "../api/types";

export const useAttachments = () => {
  return useQuery<AttachmentDto[]>({
    queryKey: ["attachments"],
    queryFn: getAttachments,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });
};

export const usePendingAttachments = () => {
  return useQuery<AttachmentDto[]>({
    queryKey: ["attachments", "pending"],
    queryFn: getPendingAttachments,
    staleTime: 30 * 1000,
  });
};

export const useDeleteAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAttachment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attachments"] });
    },
  });
};

export const useUpdateAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateAttachmentDto }) =>
      updateAttachment(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attachments"] });
    },
  });
};

/** Swap an attachment's file for another, keeping its id (see the API doc). */
export const useReplaceAttachmentContent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      file,
      filename,
      contentType,
      preview,
    }: {
      id: string;
      file: File | Blob;
      filename: string;
      contentType: AttachmentType;
      preview?: Blob;
    }) => replaceAttachmentContent(id, file, filename, contentType, preview),
    onSuccess: (updated) => {
      // The file's URL carries `previewVersion`, which the replacement bumped.
      // Put it in the cache now, so anything reading the file right after the
      // mutation resolves (auto-fill carries straight on) builds the new URL
      // rather than the old one until the refetch lands.
      queryClient.setQueryData<AttachmentDto[]>(["attachments"], (list) =>
        list?.map((a) => (a.id === updated.id ? updated : a)),
      );
      queryClient.invalidateQueries({ queryKey: ["attachments"] });
    },
  });
};

export const useUploadAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      filename,
      contentType,
      preview,
    }: {
      file: File | Blob;
      filename: string;
      contentType: AttachmentType;
      preview?: Blob;
    }) => uploadAttachment(file, filename, contentType, preview),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attachments"] });
    },
  });
};
